import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Clock } from "lucide-react";

const IDLE_MS = 30 * 60 * 1000;
const WARN_MS = 60 * 1000;
const KEY = "hs_last_active";
const STAFF = new Set(["recruiter", "admin", "super_admin"]);

// Signs recruiters/admins out after 30 idle minutes (shared across tabs), with a 1-minute warning.
export function IdleSignOut() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [left, setLeft] = useState(null);
  const lastWrite = useRef(0);
  const outRef = useRef(null);
  outRef.current = () => { logout(); setLeft(null); nav("/login?reason=idle"); };
  const active = !!user && STAFF.has(user.role);

  useEffect(() => {
    if (!active) { setLeft(null); return undefined; }
    const bump = () => { const now = Date.now(); if (now - lastWrite.current > 5000) { lastWrite.current = now; localStorage.setItem(KEY, String(now)); } };
    localStorage.setItem(KEY, String(Date.now()));
    const evs = ["mousemove", "keydown", "mousedown", "scroll", "touchstart"];
    evs.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const t = setInterval(() => {
      const idle = Date.now() - Number(localStorage.getItem(KEY) || Date.now());
      if (idle >= IDLE_MS) outRef.current();
      else if (idle >= IDLE_MS - WARN_MS) setLeft(Math.ceil((IDLE_MS - idle) / 1000));
      else setLeft(null);
    }, 1000);
    return () => { clearInterval(t); evs.forEach((e) => window.removeEventListener(e, bump)); };
  }, [active]);

  if (left == null) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-6" data-testid="idle-warning">
      <div className="max-w-sm w-full rounded-2xl border border-border bg-card p-6 text-center space-y-3">
        <Clock className="h-8 w-8 text-amber-400 mx-auto" />
        <h2 className="font-display text-lg font-semibold">Still there?</h2>
        <p className="text-sm text-muted-foreground">For your security you'll be signed out in <strong className="text-foreground" data-testid="idle-countdown">{left}s</strong>.</p>
        <Button className="w-full" onClick={() => { localStorage.setItem(KEY, String(Date.now())); setLeft(null); }} data-testid="idle-stay">Stay signed in</Button>
      </div>
    </div>
  );
}
